import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { formatDate } from '@angular/common';
import { Injectable, signal } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { Observable, map, switchMap } from 'rxjs';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { GridService } from 'shared/interfaces/grid-service';
import { NotificationSettings } from 'shared/interfaces/notification-settings.interface';
import { CachingService } from 'shared/services/caching.service';
import { UserApiService } from 'shared/services/userApi.service';
import { User } from 'users/interfaces/user.interface';

@Injectable({
    providedIn: 'root',
})
export class SupplierUsersService implements GridService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
        private userApiService: UserApiService,
    ) {
        this.apollo = this.apolloProvider.use('usersAPI');
    }

    closeSupplierUsersModal = signal<{ shouldClose: boolean; refreshGrid: boolean }>({
        shouldClose: false,
        refreshGrid: false,
    });

    NAME = 'SupplierUsersGridService';

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.userApiService.getLoginInformation().pipe(
            switchMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                const supplierId = (userData as CurrentSupplierUserInterface)?.supplierId;

                return this.apollo
                    .query({
                        query: gql`
                        query getSupplierUsers (
                            $companyName: String,
                            $email: String,
                            $firstName: String,
                            $lastName: String,
                            $userGroup: String,
                            $listingAccessGroup: String,
                            $statuses: [UserStatus],
                            $lastLogin: DateOnly,
                            $orderBy: String,
                            $orderDirection: OrderDirection) {
                            supplierUsers {
                                search(searchSettings: {
                                    pageNumber: ${pageNumber},
                                    pageSize:${pageSize},
                                    orderBy: $orderBy,
                                    orderDirection: $orderDirection
                                },
                                    filtering: {
                                        companyName: $companyName,
                                        email: $email,
                                        firstName: $firstName,
                                        lastName: $lastName,
                                        userGroup: $userGroup,
                                        listingAccessGroup: $listingAccessGroup,
                                        statuses: $statuses,
                                        lastLogin: $lastLogin,
                                        searchTerm: "${supplierId ? supplierId : ''}"
                                    })  {
                                    data {
                                        id,
                                        userType,
                                        supplierId,
                                        ${!supplierId ? ', companyName,' : ''}
                                        email,
                                        firstName,
                                        lastName,
                                        emailsEnabled,
                                        lastLogin,
                                        status,
                                        passwordChangeRequired,
                                        listingAccessGroups {
                                            id,
                                            name,
                                            description
                                        },
                                        userAccesses {
                                            id,
                                            userGroupId
                                            userGroup {
                                                id
                                                supplierId
                                                name,
                                                isAdmin
                                            }
                                        }
                                    },
                                    totalCount
                                }
                            }}`,
                        fetchPolicy: 'no-cache',
                        variables: {
                            orderBy: params.request.sortModel[0]?.colId ?? 'companyName',
                            orderDirection: params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC',
                            companyName: (params.request.filterModel as FilterModel)['companyName']?.filter,
                            email: (params.request.filterModel as FilterModel)['email']?.filter,
                            firstName: (params.request.filterModel as FilterModel)['firstName']?.filter,
                            lastName: (params.request.filterModel as FilterModel)['lastName']?.filter,
                            userGroup: (params.request.filterModel as FilterModel)['userGroup']?.filter,
                            listingAccessGroup: (params.request.filterModel as FilterModel)['accessGroups']?.filter,
                            lastLogin: (params.request.filterModel as FilterModel)['lastLogin']?.filter
                                ? formatDate((params.request.filterModel as FilterModel)['lastLogin']?.filter, 'yyyy-MM-dd', 'en-US')
                                : undefined,
                            statuses: (params.request.filterModel as FilterModel)['status']?.filter,
                        },
                    })
                    .pipe(
                        map((response: any) => {
                            return response.data.supplierUsers.search;
                        }),
                    );
            }),
        );
    }

    public getSupplierUserById(userId: string, ignoreCurrentUser: boolean = true): Observable<User> {
        return this.apollo
            .query({
                query: gql`
                query getSupplierUserById {
                    supplierUsers {
                        search(filtering:{searchTerm: "${userId}", ignoreCurrentUser: ${ignoreCurrentUser}} ) {
                            data {
                                id,
                                supplierId,
                                companyName,
                                firstName,
                                lastName,
                                email,
                                userType,
                                emailsEnabled,
                                lastLogin,
                                status,
                                isEnabled,
                                passwordChangeRequired,
                                listingAccessGroups {
                                    id,
                                    name,
                                    description
                                },
                                hasPreviousUserGroup,
                                internalPermissions {
                                    permissionAccessZone,
                                    permissionOwner,
                                    accessType,
                                    id
                                },
                                resourcePermissions {
                                    resourceId,
                                    accessType,
                                    userGroupId,
                                    id,
                                    userGroup {
                                        name
                                    }
                                },
                                hasVisualsUIEnabled,
                                visualPermissions {
                                    id
                                    userId
                                    userGroupId
                                    type
                                    accessType
                                    isImplemented
                                    derivedVisualPermissions {
                                        visualPermissionId
                                        type
                                        isActive
                                        isImplemented
                                        id
                                    }
                                }
                                userAccesses{
                                    userGroupId
                                    userGroup {
                                        name
                                    }
                                }
                                notificationSettings {
                                    id
                                    userId
                                    owner
                                    isActive
                                    notificationType
                                },
                                entityTracking {
                                    createdBy,
                                    createdAt,
                                    modifiedBy,
                                    modifiedAt
                                }
                            },
                            totalCount
                        }
                    }}`,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierUsers.search.data[0];
                }),
            );
    }

    public getUsersBySupplierId(supplierId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query allSupplierUsers {
                    supplierUsers {
                        search(filtering: {searchTerm: "${supplierId}"},
                            searchSettings: {pageSize: 1000}
                        ) {
                            data {
                                id,
                                userType,
                                supplierId,
                                companyName,
                                email,
                                firstName,
                                lastName,
                                emailsEnabled,
                                lastLogin,
                                userAccesses {
                                    id,
                                    userGroupId
                                    userGroup {
                                        id
                                        supplierId
                                        name
                                    }
                                }
                            },
                            totalCount
                        }
                    }}`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierUsers.search.data;
                }),
            );
    }

    public updateCurrentSupplierUser(user: User): Observable<any> {
        const supplierUserMutation = `
                mutation updateCurrentSupplierUser {
                    supplierUsers {
                        updateCurrentSupplierUser(
                            supplierUser: {
                                ${user.firstName ? 'firstName: "' + user.firstName + '"' : ''},
                                ${user.lastName ? 'lastName: "' + user.lastName + '"' : ''}
                            }) {
                            statusCode,
                            message
                        }
                    }
                }`;
        return this.apollo
            .mutate({
                mutation: gql(supplierUserMutation),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierUsers.updateCurrentSupplierUser;
                }),
            );
    }

    public updateSupplierUser(user: User): Observable<any> {
        const listingAccessGroupsInput =
            user.listingAccessGroupsInput
                ?.filter(Boolean)
                .map((id) => `"${id}"`)
                .join() ?? String.EMPTY;
        const userMutation = `
                mutation updateSupplierUser {
                    supplierUsers {
                        updateSupplierUser(
                            supplierUser: {
                                id: "${user.id}",
                                ${user.firstName ? 'firstName: "' + user.firstName + '"' : ''},
                                ${user.lastName ? 'lastName: "' + user.lastName + '"' : ''},
                                ${user.isEnabled !== null && user.isEnabled !== undefined ? 'isEnabled: ' + user.isEnabled : ''},
                                ${user.emailsEnabled !== null && user.emailsEnabled !== undefined ? 'emailsEnabled: ' + user.emailsEnabled : ''},
                                ${user.listingAccessGroupsInput !== undefined ? 'listingAccessGroupsInput: [' + listingAccessGroupsInput + ']' : ''}
                                ${user.passwordChangeRequired !== null && user.passwordChangeRequired !== undefined ? 'passwordChangeRequired: ' + user.passwordChangeRequired : ''},
                            }) {
                            statusCode,
                            message
                        }
                    }
                }`;

        return this.apollo
            .mutate({
                mutation: gql(userMutation),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierUsers.updateSupplierUser;
                }),
            );
    }

    public restorePreviousUserAccess(supplierUserId: string): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation restorePreviousUserAccess {
                    userAccess {
                        reactivateAccess(supplierUserId:"${supplierUserId}"){
                            data,
                            statusCode
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userAccess.reactivateAccess.data;
                }),
            );
    }

    public moveUserToGroup(supplierUserId: string, userGroupId: string): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation moveUserToGroup {
                    userAccess {
                        createAccess(userAccess:{userId:"${supplierUserId}",userGroupId:"${userGroupId}"}){
                            data,
                            statusCode,
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userAccess.createAccess.data;
                }),
            );
    }

    public deleteSupplierUser(user: User): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation deleteUser {
                    supplierUsers {
                        deleteSupplierUser(supplierUserID: "${user.id}") {
                            data,
                            statusCode,
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.supplierUsers.deleteSupplierUser.data;
                }),
            );
    }

    //Methods for notification settings
    public getNotificationSettingsByUserId(userId: string | string[]): Observable<NotificationSettings[]> {
        let userIds;
        if (!Array.isArray(userId)) {
            userIds = [userId] as string[];
        } else {
            userIds = userId as string[];
        }

        return this.apollo
            .mutate({
                mutation: gql`
                query getNotificationSettings {
                    notificationSettings {
                        search(filtering:{
                            userIds:[${userIds.map((id) => `"${id}"`)}]
                        }){
                            data {
                                id,
                                userId,
                                notificationType,
                                owner,
                                isActive
                            },
                            totalCount,
                            statusCode,
                            message
                        }
                    }
                }`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.notificationSettings.search.data;
                }),
            );
    }

    public updateNotificationSettings(
        notification: NotificationSettings | NotificationSettings[],
        isCurrentUser: boolean = false,
    ): Observable<any> {
        let notifications;
        if (!Array.isArray(notification)) {
            notifications = [notification] as NotificationSettings[];
        } else {
            notifications = notification as NotificationSettings[];
        }

        let array = '';

        for (let i = 0; i < notifications.length; i++) {
            array += `{ id: "${notifications[i].id}"
                        userId: "${notifications[i].userId}",
                        isActive: ${notifications[i].isActive}
                        },`;
        }

        const updateNotifications = `
                mutation ${isCurrentUser ? 'updateCurrentUserNotificationSettings' : 'updateNotificationSettings'} {
                    notificationSettings {
                        ${isCurrentUser ? 'updateCurrentUserNotificationSettings' : 'updateMultipleNotificationSettings'} (
                            notificationSettings: [
                            ${array}
                            ]) {
                            statusCode,
                            message
                        }
                    }
                }`;

        return this.apollo
            .mutate({
                mutation: gql(updateNotifications),
            })
            .pipe(
                map((response: any) => {
                    return isCurrentUser
                        ? response.data.notificationSettings.updateCurrentUserNotificationSettings.data
                        : response.data.notificationSettings.updateMultipleNotificationSettings.data;
                }),
            );
    }

    //Apollo cache
    public getSupplierUserFromCache(userId: string | undefined) {
        return (
            this.apollo.client.cache.readQuery({
                query: gql`
                query readSupplierUser($id: ID!) {
                    supplierUsers{
                        search(filtering:{searchTerm: "${userId}"} ) {
                            data {
                                id
                                firstName
                                lastName
                                email
                                userType
                            }
                        }
                    }
                }
            `,
                variables: {
                    id: userId,
                },
            }) as any
        )?.supplierUsers?.search?.data[0];
    }
}
